import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import SiteVisitForm from "@/components/SiteVisitForm";
import { ShieldCheck } from "lucide-react";

const VENDING_IMG =
  "/media/vending-machine-orange-wall.png";

const SECTIONS = [
  {
    title: "1. About These Terms",
    body: (
      <p>
        By using this website or requesting a service from Anytime Vending Machines, you agree to these Terms &amp; Conditions. If you do not agree, please do not use the website or submit a booking request.
      </p>
    ),
  },
  {
    title: "2. Eligibility for Free Installation",
    body: (
      <>
        <p>
          Free vending machine installation is generally available to sites with <b className="text-white">30+ staff</b> or <b className="text-white">steady foot traffic</b>. Eligibility depends on site suitability, access, location, and expected usage.
        </p>
        <ul className="mt-3 list-disc pl-5 space-y-1.5">
          <li>Staff numbers are estimates provided by the site</li>
          <li>Foot traffic includes visitors, contractors, drivers, customers, and members</li>
          <li>Final approval occurs after assessment and/or site visit</li>
        </ul>
      </>
    ),
  },
  {
    title: "3. Site Visit & Assessment",
    body: (
      <p>
        We may conduct a site visit to confirm eligibility and practical requirements, including placement, access, safety, and power. By booking a site visit, you agree to provide reasonable site access and information to support assessment.
      </p>
    ),
  },
  {
    title: "4. Installation Requirements",
    body: (
      <>
        <p>
          Installation requires appropriate access and a standard power point near the placement area. Where required, the site may need to assist with access arrangements (e.g., loading bay access, stair limitations, or forklift/trolley availability).
        </p>
        <ul className="mt-3 list-disc pl-5 space-y-1.5">
          <li>Machine placement must meet safety and access requirements</li>
          <li>We may refuse installation if access or safety is unsuitable</li>
          <li>Electrical testing/tagging may be performed as part of the service</li>
        </ul>
      </>
    ),
  },
  {
    title: "5. Ownership of Equipment",
    body: (
      <p>
        Unless otherwise agreed in writing, the vending machine remains the property of the vending operator / supplier. The site receives the benefit of on-site vending access while the machine is installed.
      </p>
    ),
  },
  {
    title: "6. Stocking, Product Range & Pricing",
    body: (
      <>
        <p>
          We will select and supply products based on availability, site preferences, and demand. While we welcome requests, we cannot guarantee availability of every product.
        </p>
        <ul className="mt-3 list-disc pl-5 space-y-1.5">
          <li>Product mix may change over time based on usage and supply</li>
          <li>Healthy options may be provided on request where available</li>
          <li>Product pricing is set by the vending operator and may change</li>
        </ul>
      </>
    ),
  },
  {
    title: "7. Servicing, Access & Site Cooperation",
    body: (
      <>
        <p>
          The site agrees to provide reasonable access for restocking and servicing. If access is repeatedly restricted, we may suspend service or remove the machine.
        </p>
        <ul className="mt-3 list-disc pl-5 space-y-1.5">
          <li>Provide safe access during business hours or agreed times</li>
          <li>Do not block or relocate the machine without approval</li>
          <li>Report faults promptly so we can respond</li>
        </ul>
      </>
    ),
  },
  {
    title: "8. Trial & Removal",
    body: (
      <>
        <p>
          Some sites may be offered a <b className="text-white">no-obligation trial</b>. If usage is low or the service is not suitable, the machine may be removed.
        </p>
        <ul className="mt-3 list-disc pl-5 space-y-1.5">
          <li>Removal may occur at the operator's discretion (e.g., low usage, safety issues)</li>
          <li>We may arrange a suitable collection time with the site</li>
          <li>No penalties are generally applied for removal unless damage occurs</li>
        </ul>
      </>
    ),
  },
  {
    title: "9. Damage, Misuse & Liability",
    body: (
      <>
        <p>
          The site should take reasonable steps to prevent damage or misuse of the machine. We are not responsible for losses caused by misuse, vandalism, or unauthorised tampering.
        </p>
        <ul className="mt-3 list-disc pl-5 space-y-1.5">
          <li>Do not force doors, coin slots, or payment terminals</li>
          <li>Do not unplug the machine without agreement</li>
          <li>Report vandalism or repeated misuse</li>
        </ul>
      </>
    ),
  },
  {
    title: "10. Website Use",
    body: (
      <p>
        Website content is provided for general information and may change without notice. You agree not to misuse the website, attempt unauthorised access, or interfere with website functionality.
      </p>
    ),
  },
  {
    title: "11. Privacy",
    body: (
      <p>
        We handle personal information in accordance with our Privacy Policy. If you submit information via forms, you consent to us using it to respond to your enquiry and provide services.
      </p>
    ),
  },
  {
    title: "12. Changes to These Terms",
    body: (
      <p>
        We may update these Terms &amp; Conditions from time to time. The latest version will be displayed on this page.
      </p>
    ),
  },
  {
    title: "13. General",
    body: (
      <p>
        These Terms are intended to operate to the maximum extent permitted by law. If any part is found unenforceable, the remaining provisions continue in effect.
      </p>
    ),
  },
];

export default function Terms() {
  return (
    <div data-testid="terms-page" className="text-white">
      <Seo />
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-radial-fade" />
        <div className="relative mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center rounded-full border border-[#FE7200]/30 bg-[#FE7200]/10 px-3 py-1 text-xs font-semibold tracking-wide text-[#FE7200]">
              Anytime Vending Machines
            </div>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
              Terms &amp; Conditions
            </h1>
            <p className="mt-5 text-base text-zinc-300 max-w-2xl">
              These Terms &amp; Conditions apply to the use of our website and our vending machine services across Australia, United Kingdom, USA and Canada including free vending machine installations for qualifying sites.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="#terms-body"
                data-testid="terms-read-button"
                className="inline-flex items-center justify-center rounded-full bg-[#FE7200] px-6 py-3 text-sm font-bold uppercase tracking-wider text-black hover:bg-[#FF8C2E] transition-colors"
              >
                Read Terms
              </a>
              <Link
                to="/"
                data-testid="terms-home-button"
                className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-bold uppercase tracking-wider text-white hover:bg-white/10 transition-colors"
              >
                Back To Home
              </Link>
            </div>
            <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-300">
              {["Clear expectations", "Safety-first installs", "No lock-in (where stated)"].map((b) => (
                <li key={b} className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-[#FE7200]" /> {b}
                </li>
              ))}
            </ul>
          </div>

          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 rounded-2xl border border-white/10 bg-[#121212] overflow-hidden">
              <img src={VENDING_IMG} alt="Vending machine" className="w-full h-44 object-cover opacity-90" />
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-xl font-bold text-white">Quick summary</h3>
                  <ShieldCheck className="h-5 w-5 text-[#FE7200]" />
                </div>
                <p className="mt-1 text-sm text-zinc-400">Plain-English highlights (full details below).</p>
                <ul className="mt-4 space-y-2 text-sm text-zinc-300 list-disc pl-5">
                  <li>Eligibility is based on staff numbers and/or foot traffic</li>
                  <li>Machines remain the property of the vending operator unless otherwise agreed</li>
                  <li>We manage stocking, servicing, and product range (with input from you)</li>
                  <li>We can remove a machine if usage is low or site requirements change</li>
                  <li>Site access and safety requirements apply for installs and servicing</li>
                </ul>
                <a
                  href="#book-form"
                  data-testid="terms-book-button"
                  className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-[#FE7200] px-5 py-3 text-xs font-bold uppercase tracking-[0.18em] text-black hover:bg-[#FF8C2E] transition-colors"
                >
                  Book A Free Site Visit
                </a>
                <p className="mt-3 text-xs text-zinc-500 text-center">
                  If you have questions, submit the form at the bottom and we'll reply shortly.
                </p>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section id="terms-body" className="mx-auto max-w-5xl px-4 md:px-8 lg:px-12 py-16" data-testid="terms-sections">
        <div className="space-y-6">
          {SECTIONS.map((s, i) => (
            <section
              key={i}
              data-testid={`terms-section-${i + 1}`}
              className="rounded-2xl border border-white/10 bg-[#121212] p-6 md:p-7"
            >
              <h2 className="font-display text-xl md:text-2xl font-bold text-white">{s.title}</h2>
              <div className="mt-3 text-sm md:text-base text-zinc-300 leading-relaxed">{s.body}</div>
            </section>
          ))}
        </div>
      </section>

      <section id="book-form" className="mx-auto max-w-5xl px-4 md:px-8 lg:px-12 pb-20" data-testid="terms-form-section">
        <SiteVisitForm source="terms-page" testIdPrefix="terms-booking" />
      </section>
    </div>
  );
}
